/**
 * SyllabusSwitcher — 강의 머리의 「강의 바꾸기」 판(설계 FE-20260912 §W01·§6).
 *
 * 범위 탭(전체·내 강의·공유받은 강의) + 이름 찾기 + 목록 + 쪽 이동 + [새 강의].
 * [새 강의]는 canCreate 로 끈다 — 시험지 조립 창의 강의 칸은 고르기만 한다(2026-09-14, 빈 강의를 만들어도 찾을 조건이 없다).
 * 쪽 이동을 둔 이유: 옛 목록은 page 없이 불러 100개 뒤의 강의에 닿을 수 없었다(설계 §6·수용 기준 13).
 * 한 쪽 30개 — 떠 있는 작은 판이라 100개를 한 번에 그리면 스크롤만 길어진다.
 * 서버 q 는 **강의 이름 부분일치만** 된다(설계 §W01) — 그래서 입력칸 이름도 「이름으로 찾기」다.
 *
 * 비동기(async-boundary): 요청마다 세대 번호 — 탭·검색어·쪽을 빨리 바꿔도 늦게 온 앞 응답이 지금 목록을 덮지 않는다.
 * 실패하면 지난 목록을 두고 사유와 [다시 시도]를 덧붙인다. 검색어는 멈춘 뒤 0.3초에 한 번(글자마다 부르지 않는다).
 */
import {
  useEffect, useRef, useState,
} from 'react';
import {
  ChevronLeft, ChevronRight, Plus, Search, Users,
} from 'lucide-react';
import { Button } from '../common';
import FloatingPanel from './FloatingPanel';
import { listSyllabi, createSyllabus, describeSyllabusError } from './syllabusApi';
import { shareLabel, panelErrorCls } from './SharePopover';

export const SWITCH_PER_PAGE = 30;
const SCOPE_TABS = [['all', '전체'], ['mine', '내 강의'], ['shared', '공유받은 강의']];

const ringCls = 'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary';
// 범위 탭 — 옛 강의 목록(Syllabus.js tabCls)과 같은 값
const tabCls = (on) => `whitespace-nowrap px-3 py-1 text-xs transition-colors ${ringCls} ${on ? 'bg-primary font-medium text-white' : 'text-gray-500 hover:bg-gray-200'}`;
const pagerCls = `inline-flex items-center gap-0.5 rounded-full px-2 py-1 text-xs text-gray-500 transition-colors hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-40 ${ringCls}`;
const itemCls = `flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left text-xs text-gray-700 transition-colors hover:bg-gray-50 ${ringCls}`;

function SyllabusSwitcher({
  anchorRef, currentId = null, getAccessToken, onSwitch, onCreated, onClose, sheet = false, canCreate = true,
}) {
  const [scope, setScope] = useState('all');
  const [q, setQ] = useState('');
  const [query, setQuery] = useState(''); // 멈춘 뒤 확정한 검색어 — 목록 요청은 이것만 본다
  const [req, setReq] = useState({ page: 1, n: 0 }); // n — 같은 쪽을 다시 부를 때(다시 시도) 올린다
  const [list, setList] = useState({
    loading: true, items: [], page: 1, totalPages: 1, totalItems: 0, error: null,
  });
  const [creating, setCreating] = useState(null); // null | 입력 중 이름
  const [createBusy, setCreateBusy] = useState(false);
  const [createError, setCreateError] = useState(null);
  const gen = useRef(0);
  const aliveRef = useRef(true);
  const createBusyRef = useRef(false);
  const createInputRef = useRef(null);
  const newBtnRef = useRef(null);
  const refocusNew = useRef(false);

  // 효과 안에서 다시 켠다 — StrictMode(개발 서버)는 효과를 걸었다 풀었다 다시 걸어서, 끄기만 하면 꺼진 채 남아 목록이 영영 「불러오는 중」이다
  useEffect(() => {
    aliveRef.current = true;
    return () => { aliveRef.current = false; };
  }, []);

  // 검색어는 멈춘 뒤 0.3초에 — 확정하면 1쪽부터
  useEffect(() => {
    const t = q.trim();
    if (t === query) return undefined;
    const timer = setTimeout(() => {
      setQuery(t);
      setReq((r) => ({ page: 1, n: r.n + 1 }));
    }, 300);
    return () => clearTimeout(timer);
  }, [q, query]);

  useEffect(() => {
    gen.current += 1;
    const g = gen.current;
    const mine = () => aliveRef.current && g === gen.current;
    setList((s) => ({ ...s, loading: true, error: null }));
    listSyllabi({
      scope, q: query, page: req.page, perPage: SWITCH_PER_PAGE,
    }, getAccessToken)
      .then((d) => {
        if (!mine()) return;
        setList({
          loading: false,
          items: d.items,
          page: d.page ?? req.page,
          totalPages: Math.max(1, d.totalPages || 1),
          totalItems: d.totalItems ?? d.items.length,
          error: null,
        });
      })
      .catch((e) => {
        // 지난 목록은 두고 실패만 덧붙인다 — 쪽 표시도 마지막으로 받은 쪽 그대로(요청한 쪽이 아니라)
        if (mine()) setList((s) => ({ ...s, loading: false, error: describeSyllabusError(e) }));
      });
  }, [scope, query, req, getAccessToken]);

  const changeScope = (k) => {
    if (k === scope) return;
    setScope(k);
    setReq((r) => ({ page: 1, n: r.n + 1 }));
  };
  const goPage = (p) => setReq((r) => ({ page: p, n: r.n + 1 }));
  const retry = () => setReq((r) => ({ ...r, n: r.n + 1 }));

  const pick = (s) => {
    onClose('picked');
    if (String(s.syllabus_id) !== String(currentId)) onSwitch?.(String(s.syllabus_id));
  };

  // [새 강의]를 누르면 이름 칸으로, 취소하면 [새 강의]로 초점을 돌린다(칸이 사라지면 초점이 body 로 떨어진다)
  const isCreating = creating != null;
  useEffect(() => {
    if (isCreating) createInputRef.current?.focus();
    else if (refocusNew.current) { refocusNew.current = false; newBtnRef.current?.focus(); }
  }, [isCreating]);
  const cancelCreate = () => { refocusNew.current = true; setCreating(null); setCreateError(null); };

  const submitCreate = async (ev) => {
    ev.preventDefault();
    const name = (creating || '').trim();
    if (!name || createBusyRef.current) return;
    createBusyRef.current = true;
    setCreateBusy(true);
    setCreateError(null);
    try {
      const d = await createSyllabus({ name }, getAccessToken);
      if (!aliveRef.current) return;
      onClose('created');
      onCreated?.(String(d.syllabus_id));
    } catch (e) {
      if (aliveRef.current) setCreateError(describeSyllabusError(e));
    } finally {
      createBusyRef.current = false;
      if (aliveRef.current) setCreateBusy(false);
    }
  };

  const emptyText = () => {
    if (query) return '이름이 맞는 강의가 없습니다.';
    if (scope === 'shared') return '공유받은 강의가 없습니다.';
    const make = canCreate ? ' — [새 강의]로 만드세요.' : '.';
    if (scope === 'mine') return `내 강의가 없습니다${make}`;
    return `강의가 없습니다${make}`;
  };

  const { items } = list;
  const showPager = list.totalPages > 1 || list.page > 1;

  return (
    <FloatingPanel anchorRef={anchorRef} onClose={onClose} align="start" sheet={sheet} label="강의 바꾸기" className={sheet ? '' : 'w-80'}>
      <div className="space-y-1.5 p-1">
        {/* 이름 칸이 판의 첫 입력이어야 열자마자 칠 수 있다(FloatingPanel 은 DOM 순서로 첫 입력에 초점을 준다) */}
        <label className="relative block">
          <Search className="pointer-events-none absolute left-2 top-1/2 size-3.5 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            aria-label="강의 이름으로 찾기"
            placeholder="이름으로 찾기"
            className="w-full rounded-lg border border-gray-200 bg-white py-1.5 pl-7 pr-2 text-xs focus:border-primary focus:outline-none"
          />
        </label>
        <div className="flex overflow-hidden rounded-full bg-gray-100" role="tablist" aria-label="강의 범위">
          {SCOPE_TABS.map(([k, label]) => (
            <button key={k} type="button" role="tab" aria-selected={scope === k} onClick={() => changeScope(k)} className={`flex-1 ${tabCls(scope === k)}`}>
              {label}
            </button>
          ))}
        </div>
      </div>

      {list.error && (
        <div role="alert" className={`mx-1 mb-1 flex items-start gap-2 ${panelErrorCls}`}>
          <span className="min-w-0 flex-1">{list.error}</span>
          <button type="button" onClick={retry} className={`shrink-0 rounded underline hover:text-red-700 ${ringCls}`}>다시 시도</button>
        </div>
      )}

      <div aria-busy={list.loading} className={`max-h-64 overflow-y-auto transition-opacity ${list.loading && items.length ? 'opacity-60' : ''}`}>
        {list.loading && !items.length && <p className="px-2 py-4 text-center text-xs text-gray-400">강의를 불러오는 중…</p>}
        {!list.loading && !list.error && !items.length && <p className="px-2 py-4 text-center text-xs text-gray-400">{emptyText()}</p>}
        <ul className="space-y-0.5">
          {items.map((s) => {
            const here = String(s.syllabus_id) === String(currentId);
            return (
              <li key={s.syllabus_id} className={here ? 'rounded-xl bg-primary/10' : ''}>
                <button
                  type="button"
                  onClick={() => pick(s)}
                  aria-current={here ? 'true' : undefined}
                  className={`w-full rounded-xl px-2 py-1.5 text-left transition-colors ${ringCls} ${here ? '' : 'hover:bg-gray-50'}`}
                >
                  <span className="flex min-w-0 items-center gap-1.5">
                    <span className={`min-w-0 flex-1 truncate text-xs ${here ? 'font-bold text-primary' : 'font-medium text-gray-900'}`} title={s.name}>{s.name}</span>
                    {!s.is_mine && <Users aria-label="공유받은 강의" className="size-3.5 shrink-0 text-gray-400" />}
                    <span className="shrink-0 rounded-full bg-gray-100 px-2 py-0.5 text-[10px] text-gray-500">{shareLabel(s.share_scope)}</span>
                  </span>
                  <span className="mt-0.5 flex flex-wrap gap-x-2 text-[11px] text-gray-400">
                    <span>행 {s.node_count ?? 0}개</span>
                    {!s.is_mine && s.author_name && <span>{s.author_name}</span>}
                    {here && <span className="font-medium text-primary">지금 강의</span>}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      </div>

      {showPager && (
        <div className="flex items-center justify-between px-1 pt-1">
          <button type="button" onClick={() => goPage(list.page - 1)} disabled={list.loading || list.page <= 1} className={pagerCls}>
            <ChevronLeft className="size-3.5" /> 이전
          </button>
          <span className="text-[11px] text-gray-500" aria-live="polite">{list.page} / {list.totalPages} · {list.totalItems}개</span>
          <button type="button" onClick={() => goPage(list.page + 1)} disabled={list.loading || list.page >= list.totalPages} className={pagerCls}>
            다음 <ChevronRight className="size-3.5" />
          </button>
        </div>
      )}

      {canCreate && (
        <div className="mt-1 border-t border-gray-100 pt-1">
          {!isCreating ? (
            <button ref={newBtnRef} type="button" onClick={() => { setCreating(''); setCreateError(null); }} className={itemCls}>
              <Plus className="size-3.5 shrink-0 text-gray-400" /> 새 강의
            </button>
          ) : (
            <form onSubmit={submitCreate} className="flex items-center gap-1 p-1">
              <input
                ref={createInputRef}
                type="text"
                value={creating}
                onChange={(e) => setCreating(e.target.value)}
                onKeyDown={(e) => {
                  // 한글 조합 중 Enter 는 글자 확정이다 — 만들기로 보내지 않는다
                  if (e.key === 'Enter' && e.nativeEvent.isComposing) { e.preventDefault(); return; }
                  // Escape 는 이 칸만 닫는다 — preventDefault 로 FloatingPanel 이 판까지 닫지 않게 하고, 전파도 멈춘다
                  // (FloatingPanel 은 막힌 Escape 를 건너뛸 뿐 전파를 멈추지 않아, 그대로 두면 판 밖 덮개·서랍의 Escape 까지 닿는다)
                  if (e.key === 'Escape' && !e.nativeEvent.isComposing) { e.preventDefault(); e.stopPropagation(); cancelCreate(); }
                }}
                disabled={createBusy}
                maxLength={120}
                placeholder="강의 이름(1~120자)"
                aria-label="새 강의 이름"
                className="min-w-0 flex-1 rounded-lg border border-gray-200 bg-white px-2 py-1.5 text-xs focus:border-primary focus:outline-none disabled:bg-gray-50"
              />
              <Button size="xs" type="submit" loading={createBusy} disabled={!creating.trim()}>만들기</Button>
              <Button size="xs" variant="secondary" onClick={cancelCreate} disabled={createBusy}>취소</Button>
            </form>
          )}
          {createError && <p role="alert" className={`mx-1 mb-1 ${panelErrorCls}`}>{createError}</p>}
        </div>
      )}
    </FloatingPanel>
  );
}

export default SyllabusSwitcher;
